"use client";

import { cn } from "@/lib/utils";
import {
  ChevronsDown as ChevronsDownIcon,
  Download as DownloadIcon,
} from "lucide-react";
import * as React from "react";
import RightArrow from "../../assets/RightArrow";
import Image from "next/image";

export const classNameStyles = {
  default: "bg-secondary-main py-2 px-4 rounded-lg",
  tertiary: "bg-gray-1 py-2 px-4 rounded-lg ",
  large:
    "bg-secondary-main py-2 px-5 flex items-center gap-[10px] !text-color-white text-heading-6-semibold rounded-lg",
  primary:
    "bg-primary-main py-2 px-5 flex items-center gap-[10px] !text-color-white text-heading-7-semibold rounded-lg",
  card: "bg-secondary-main py-[6px] px-[10px] flex items-center gap-1 !text-color-white text-heading-6-semibold rounded-lg",
  outline:
    "border border-secondary-main py-[6px] px-[10px] flex items-center gap-1 text-heading-6-semibold rounded-lg",
  ghost: "",
  admin:
    "bg-primary-main text-white hover:bg-primary-main/90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-main focus-visible:ring-offset-2 h-10 px-4 py-2",
} as const;

type StyleKey = keyof typeof classNameStyles;

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  classType?: StyleKey;
  withArrow?: boolean;
  arrowVariant?: "primary" | "secondary" | "tertiary";
  withDownload?: boolean;
  withChevronsDown?: boolean;
  downloadVariant?: "primary" | "secondary";
  arrowFill?: string;
  downloadIcon?: React.ReactNode;
  arrowIcon?: React.ReactNode;
  buttonIcon?: React.ReactNode;
  buttonIconAlt?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      classType = "default",
      withArrow = false,
      withDownload = false,
      arrowVariant,
      withChevronsDown = false,
      children,
      arrowFill,
      downloadIcon,
      arrowIcon,
      buttonIcon,
      buttonIconAlt,
      ...props
    },
    ref
  ) => {
    const classes = cn(
      // baseline styles
      "inline-flex items-center justify-center whitespace-nowrap gap-spacing-4 cursor-pointer",
      // variant styles
      classType ? classNameStyles[classType] : undefined,
      // allow overrides
      className
    );

    return (
      <button ref={ref} className={classes} {...props}>
        {withDownload &&
          (downloadIcon ? (
            downloadIcon
          ) : (
            <DownloadIcon className={"w-6 h-6 text-secondary-main "} />
          ))}
        {children}
        {withArrow &&
          (arrowIcon ? (
            typeof arrowIcon === "string" ? (
              <Image
                src={arrowIcon}
                alt={buttonIconAlt || ""}
                width={20}
                height={20}
              />
            ) : (
              arrowIcon
            )
          ) : (
            <RightArrow
              fill={arrowFill || "#F74958"}
              className={cn({
                "w-8 h-8": arrowVariant === "primary",
                "w-6 h-6": arrowVariant !== "primary",
              })}
            />
          ))}
        {withChevronsDown && (
          <ChevronsDownIcon className="md:w-8 md:h-8 w-6 h-6" />
        )}
      </button>
    );
  }
);

Button.displayName = "Button";
